import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Users, GraduationCap, TrendingUp } from "lucide-react";
import { SwooshRibbons } from "@/components/SwooshRibbons";
import { scrollToId } from "@/lib/scroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const tagline = [
  { icon: Users, label: "Lead Re-engagement" },
  { icon: GraduationCap, label: "Enrolments" },
  { icon: TrendingUp, label: "Outsourced Sales" },
];

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#0B0A09] pt-36 pb-24 sm:pt-44 sm:pb-32"
    >
      <div className="pointer-events-none absolute -top-40 right-0 h-[30rem] w-[30rem] rounded-full bg-blush/[0.05] blur-[130px]" />

      <SwooshRibbons
        idPrefix="hero-tl"
        className="absolute -left-24 -top-10 h-[420px] w-[630px] opacity-[0.22]"
        delay={0.6}
      />
      <SwooshRibbons
        idPrefix="hero-br"
        flip
        className="absolute -bottom-16 -right-24 h-[420px] w-[630px] opacity-90"
        delay={0.9}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="overline-tag mb-8"
            data-testid="hero-overline"
          >
            Senior Sales &amp; Commercial Growth Consultancy — Australia
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-brand text-4xl font-medium leading-[1.15] tracking-tight sm:text-6xl lg:text-6xl"
          >
            <MaskedLine delay={0.2}>Sales and</MaskedLine>
            <MaskedLine delay={0.32}>Commercial Growth.</MaskedLine>
            <MaskedLine delay={0.44}>
              <span className="bg-gradient-to-br from-[#F0564F] to-[#C8102E] bg-clip-text text-transparent">
                Outsourced.
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-lede"
          >
            <strong className="font-semibold text-slate-100">
              The sales capability you add to your team, without the hire.
            </strong>{" "}
            We plug into your business to build and run outbound that fills your pipeline, then
            sharpen the commercial engine so more conversations close.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.78 }}
            className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3"
            data-testid="hero-tagline"
          >
            {tagline.map((t, i) => {
              const Icon = t.icon;
              return (
                <span key={t.label} className="flex items-center gap-5">
                  {i > 0 && <span className="hidden h-4 w-px bg-white/20 sm:block" />}
                  <span className="flex items-center gap-2.5">
                    <Icon className="h-4 w-4 text-blush" />
                    <span className="font-heading text-[11px] font-medium uppercase tracking-[0.22em] text-slate-300">
                      {t.label}
                    </span>
                  </span>
                </span>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-enquire"
              onClick={() => scrollToId("#enquire")}
              className="btn-magnetic group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E5484D] to-[#C8102E] px-8 py-4 font-heading text-base font-semibold text-white"
            >
              Enquire Now
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("#services")}
              className="group flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 font-heading text-base font-semibold text-slate-200 transition-colors duration-300 hover:border-blush/60 hover:text-white"
            >
              Explore Capabilities
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:col-span-5 lg:block"
          data-testid="hero-gem-visual"
        >
          <div className="relative">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-3/4 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E5232D]/15 blur-[110px]" />
            <img
              src="/rm-emblem.png"
              alt="Ruby Mill emblem"
              className="relative w-full select-none"
              draggable="false"
              style={{
                maskImage: "radial-gradient(circle at 50% 50%, black 55%, transparent 78%)",
                WebkitMaskImage: "radial-gradient(circle at 50% 50%, black 55%, transparent 78%)",
              }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
